import { useClubLogo } from '../lib/useClubLogo';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { publicApi, getImageUrl } from '../lib/api';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';



export default function RecentResults() {
  const LOGO_URL = useClubLogo();
  const [results, setResults] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([publicApi.getRecentGames(6), publicApi.getTeams()])
      .then(([gRes, tRes]) => { setResults(gRes.data); setTeams(tRes.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const getOpponentInfo = (game) => {
    if (game?.opponentTeamId) {
      const team = teams.find(t => t._id === game.opponentTeamId);
      if (team) return { logo: team.logoUrl ? getImageUrl(team.logoUrl) : null, short: team.shortName || game.opponent?.substring(0, 3).toUpperCase() };
    }
    return { logo: null, short: (game?.opponent || '??').substring(0, 3).toUpperCase() };
  };

  if (loading) return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3].map(i => (<div key={i} className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-6 animate-pulse"><div className="h-20 bg-white/5 rounded" /></div>))}
    </div>
  );

  if (results.length === 0) return (
    <div className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-12 text-center"><p className="text-white/30 text-sm">Noch keine Ergebnisse vorhanden</p></div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {results.map((game, index) => {
        const isWin = (game.scoreFor ?? 0) > (game.scoreAgainst ?? 0);
        const isLoss = (game.scoreFor ?? 0) < (game.scoreAgainst ?? 0);
        const ResultIcon = isWin ? TrendingUp : isLoss ? TrendingDown : Minus;
        const borderColor = isWin ? 'border-green-500/15 hover:border-green-500/25' : isLoss ? 'border-red-500/15 hover:border-red-500/25' : 'border-yellow-500/15';
        const opp = getOpponentInfo(game);

        return (
          <motion.div key={game._id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08, duration: 0.4 }}
            className={`bg-[hsl(0,0%,7%)] border ${borderColor} rounded-xl overflow-hidden transition-colors duration-200`} data-testid="recent-result-card">
            <div className={`px-4 py-2 flex items-center justify-between ${isWin ? 'bg-green-500/5' : isLoss ? 'bg-red-500/5' : 'bg-yellow-500/5'}`}>
              <div className="flex items-center gap-2">
                <ResultIcon className={`w-3.5 h-3.5 ${isWin ? 'text-green-400' : isLoss ? 'text-red-400' : 'text-yellow-400'}`} />
                <span className={`text-[10px] font-bold uppercase tracking-[0.12em] ${isWin ? 'text-green-400' : isLoss ? 'text-red-400' : 'text-yellow-400'}`}>
                  {isWin ? 'Sieg' : isLoss ? 'Niederlage' : 'Unentschieden'}
                </span>
              </div>
              <span className="text-[10px] text-white/25 font-mono-score">{game.date ? new Date(game.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : ''}</span>
            </div>
            <div className="p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-[hsl(0,0%,12%)] flex-shrink-0">
                      <img src={LOGO_URL} alt="Devils" className="w-full h-full object-contain rounded-full" style={{ background: '#141414' }} />
                    </div>
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-[hsl(0,0%,12%)] flex-shrink-0 border border-white/10 flex items-center justify-center">
                      {opp.logo ? (
                        <img src={opp.logo} alt={game.opponent} className="w-full h-full object-contain p-0.5 rounded-full" style={{ background: '#141414' }} />
                      ) : (
                        <span className="text-[8px] font-bold text-white/30" style={{ fontFamily: 'Space Grotesk' }}>{opp.short}</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>DEVILS</div>
                    <div className="text-[9px] text-white/20">vs</div>
                    <div className="text-xs font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>{game.opponent?.toUpperCase()}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-mono-score tracking-[0.05em]">{game.scoreFor ?? '-'}:{game.scoreAgainst ?? '-'}</div>
                  <div className="text-[10px] text-white/25 mt-1 uppercase tracking-wider">{game.location === 'Heim' ? 'Heimspiel' : 'Auswärts'}</div>
                </div>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
